import React from 'react';
import { useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { CaretRightIcon } from 'assets/icons';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials } from 'utils/commonUtils';
import { JobListItem } from 'containers/jobs/jobs.model';
import DateUtils from 'utils/dateUtils';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { FROM_SIDE_PANEL_ROUTE_STATE } from 'routes/constants';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { getJobStatusColor, getJobStatusText } from 'containers/jobs/utils';

interface JobsCardInterface {
  job: JobListItem;
}

const JobsCard: React.FC<JobsCardInterface> = ({ job }) => {
  const navigate = useNavigate();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { rootInvoiceId, rootReceiptId, rootPage, rootCustomerId, rootDisputeId, rootJobId, rootEstimateId } =
    useParams();

  const handleJobDetailsClick = jobId => {
    if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/jobs/${jobId}`, {
        state: FROM_SIDE_PANEL_ROUTE_STATE
      });
    }
  };

  return (
    <div
      className="flex w-full cursor-pointer items-end justify-between gap-1 border-b border-highlightGray pb-4"
      onClick={() => handleJobDetailsClick(job.id)}>
      <div className="flex w-[70%] flex-col gap-1">
        <div className="text-sbase leading-[18px] tracking-[-0.3px] text-primaryText">{job.jobNumber}</div>
        <div className="max-w-[98%] break-words text-17px font-semibold text-primaryText">{job.title}</div>
        <div className="flex items-center gap-2 text-px13 leading-[17px] text-accentText">
          {DateUtils.getDateInFormat({
            date: new Date(job?.createdAt),
            dateFormat: 'dd MMM yyyy'
          })}
          <div className="mb-[3px] h-1 w-1 rounded-full bg-accentText" />
          <CustomAvatar
            src={job.createdBy.profilePictureURL}
            label={getInitials(job.createdBy.name)}
            width="w-4"
            height="h-4"
            fontStyle="text-[9px]"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <div className="text-right">
          <div className="mb-1 text-17px font-bold tracking-[-0.3px] text-primaryText">
            {getAmountWithCurrency(job.amount)}
          </div>
          <div className="text-px13 leading-[17px]" style={{ color: getJobStatusColor(job.status) }}>
            {getJobStatusText(job.status)}
          </div>
        </div>
        <CaretRightIcon className="path-fill-current shrink-0 text-gray-1" />
      </div>
    </div>
  );
};

export default JobsCard;
